/**
 * @fileoverview Per-task notification bell (MOR-716).
 *
 * Filled/red when the current user will receive notifications on this task
 * (assignee, assignor, or watcher with at least one channel on); greyed when
 * they won't. The popover holds a watch/mute master toggle plus the six
 * category checkboxes — changing a checkbox saves a per-task "custom"
 * override just for this task.
 */

import { useMutation, useQuery } from "convex/react";
import { Bell, BellOff } from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Switch } from "@/ui/primitives/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  CategoryMatrix,
  type NotificationCategoryKey,
} from "./category-matrix";

export function TaskSubscriptionBell({
  taskId,
}: {
  taskId: Id<"jobKeyDates">;
}) {
  const sub = useQuery(api.notifications.watchers.getTaskSubscription, {
    taskId,
  });
  const setWatching = useMutation(api.notifications.watchers.setWatching);
  const setOverride = useMutation(api.notifications.watchers.setTaskOverride);
  if (!sub) return null;

  const toggleCategory = (
    key: NotificationCategoryKey,
    channel: "inApp" | "email",
  ) => {
    void setOverride({
      taskId,
      mode: "custom",
      matrix: {
        ...sub.matrix,
        [key]: { ...sub.matrix[key], [channel]: !sub.matrix[key][channel] },
      },
    });
  };

  const isParticipant = sub.reason === "assignee" || sub.reason === "assignor";
  const receiving = isParticipant ? sub.mode !== "muted" : sub.watching;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="Notification settings for this task"
        >
          {sub.subscribed ? (
            <Bell className="size-4 fill-red-500 text-red-500" />
          ) : (
            <BellOff className="size-4 text-muted-foreground" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="flex items-center justify-between pb-2">
          <div className="pr-3">
            <p className="text-sm font-medium">
              {isParticipant ? "Notifications" : "Watch this task"}
            </p>
            <p className="text-xs text-muted-foreground">
              {sub.reason === "assignee"
                ? "You're the assignee"
                : sub.reason === "assignor"
                  ? "You assigned this task"
                  : sub.watching
                    ? "You're watching"
                    : "Get notified about changes"}
            </p>
          </div>
          <Switch
            checked={receiving}
            onCheckedChange={(on) => {
              if (isParticipant) {
                // Participants can't unwatch — they mute instead.
                void setOverride({ taskId, mode: on ? "default" : "muted" });
              } else {
                void setWatching({ taskId, watching: on });
              }
            }}
          />
        </div>
        <div className="space-y-1.5 border-t pt-2">
          <CategoryMatrix
            matrix={sub.matrix}
            disabled={sub.mode === "muted"}
            onToggle={toggleCategory}
          />
          {sub.mode === "custom" && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-7 w-full text-xs"
              onClick={() => void setOverride({ taskId, mode: "default" })}
            >
              Reset to my defaults
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
